/**
 * The state a journey's CheckUp is in, as every Home surface names it. (F46)
 *
 * ONE derivation, shared by the server (`GET /api/home`) and journey-web's
 * dashboard card, so the two cannot disagree about whether a citizen has "not
 * started", is "ready to calculate" or is "calculating". Before this the rule
 * lived in two clients: journey-web had four states and citizen-mobile two —
 * mobile showed a citizen whose assessment was complete the same "Start
 * CheckUp" hero as someone who had never opened it.
 *
 * The order of the rules is the contract:
 *   1. A running calculation outranks everything, including a score already
 *      cached from the previous cycle — on a retake the old result is still
 *      there, and showing it as "calculated" hid the calculation the citizen
 *      had just started (F26).
 *   2. A score for this journey means `calculated`.
 *   3. A started assessment whose only missing step is the profile is
 *      `awaiting_profile` — the server says so (`awaitingProfile`); it is not
 *      re-derived here from the step list.
 *   4. A started assessment whose steps are all done (or that says it can be
 *      calculated) is `ready`.
 *   5. A failed calculation that cannot simply be re-run is `failed`.
 *   6. Anything else started is `in_progress`; nothing at all is `not_started`.
 *
 * @module heroes/checkup
 */
import type { CheckUpHomeState } from '@tonnie/types';

export interface CheckUpHomeStateInput {
  /** `checkup_assessments.status` of the journey's assessment; absent when there is none. */
  assessmentStatus: string | null | undefined;
  /** The server's `canCalculate` — every required step (the F33 profile step included) is done. */
  canCalculate: boolean;
  /** A calculated iQulture score exists for this journey. */
  hasScore: boolean;
  /**
   * The server's `awaitingProfile` — every assessment step is done and the
   * profile is the only thing between the citizen and their score (F33).
   * Absent reads as `false`: a server without the gate never waits on it.
   */
  awaitingProfile?: boolean;
}

export function deriveCheckUpHomeState({
  assessmentStatus,
  canCalculate,
  hasScore,
  awaitingProfile,
}: CheckUpHomeStateInput): CheckUpHomeState {
  if (assessmentStatus === 'calculating') return 'calculating';
  if (hasScore) return 'calculated';

  const started = assessmentStatus != null && assessmentStatus !== 'not_started';
  if (started && assessmentStatus !== 'completed' && awaitingProfile === true) {
    return 'awaiting_profile';
  }
  if (started && (assessmentStatus === 'completed' || canCalculate)) return 'ready';
  if (assessmentStatus === 'failed') return 'failed';
  if (started) return 'in_progress';
  return 'not_started';
}
